import { Loader2, Send } from "lucide-react";
import { useState } from "react";

import { apiErrorMessage } from "../lib/api.js";
import { REQUEST_SENT_MESSAGE } from "../lib/join-requests.js";
import { Button } from "./ui/button.js";
import { Input } from "./ui/input.js";
import { Label } from "./ui/label.js";

/**
 * "Is this store managed by an agency?" — the merchant names their agency's Ecomally email and the
 * agency is asked to approve (Flow 1). Used on the install-complete page and on Connect a store.
 *
 * The confirmation is the SAME whatever the address is, by design (the API answers the same too).
 */
export function AgencyJoinForm({
  onSubmit,
  idPrefix,
}: {
  readonly onSubmit: (agencyEmail: string) => Promise<void>;
  /** Keeps the input's id unique when the form appears twice on a page. */
  readonly idPrefix: string;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <p role="status" className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-sm dark:border-blue-900 dark:bg-blue-950/30">
        {REQUEST_SENT_MESSAGE}
      </p>
    );
  }

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // An email input's value is already trimmed by the browser (value sanitisation), and the API trims too.
      await onSubmit(email);
      setSent(true);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not send the request. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const id = `${idPrefix}-agency-email`;
  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-2">
      <Label htmlFor={id}>Your agency's email</Label>
      <div className="flex flex-wrap gap-2">
        <Input
          id={id}
          type="email"
          required
          placeholder="name@your-agency.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={busy}
          className="min-w-56 flex-1"
        />
        <Button type="submit" variant="outline" disabled={busy || email.trim() === ""}>
          {busy ? <Loader2 className="animate-spin" /> : <Send />}
          Send request
        </Button>
      </div>
      <p className="text-muted-foreground text-xs">The address they use to sign in to Ecomally. Nothing changes until they approve.</p>
      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
